/**
 * The business's logo and signature: storing them, and resolving them for print.
 *
 * WHY A DEDICATED SERVICE
 * -----------------------
 * `Business.logoAttachmentId` and `signatureAttachmentId` and a whole
 * `attachments` table already existed, and nothing read or wrote any of them. A
 * field that is stored and never rendered is worse than an absent one: the
 * Business Profile screen offers no way to set it, so nobody ever finds out it
 * does not work, and the plan carries it as "in progress" forever.
 *
 * WHY THE UPLOAD IS VALIDATED HERE AND NOT IN THE SCREEN
 * ------------------------------------------------------
 * Because the two things that go wrong with an upload are both invisible at the
 * point of failure:
 *
 *   1. SVG. It is an XML document that can carry script and external
 *      references, and this image ends up inside a generated tax invoice. A
 *      "logo" that is also an attack surface is not a logo.
 *   2. SIZE. A 40 MB PNG on every invoice is a database that stops opening, and
 *      the failure surfaces as the app being slow, somewhere unrelated.
 *
 * Rules live in one place so a second caller - the import path, a future
 * signature stamp - cannot accidentally be more permissive than the screen.
 *
 * WHY A DATA URL
 * --------------
 * `renderA4` produces standalone HTML handed to an iframe and printed. A
 * `blob:` URL would not survive that: the iframe is a fresh document with a
 * fresh origin, and a blob URL minted in the parent is unreadable from it. The
 * printed invoice would silently show a broken image where the logo should be -
 * on a tax invoice, with no error anywhere. So the blob is INLINED as a data URL
 * before it reaches the renderer, and the renderer stays pure and synchronous,
 * exactly like the rest of `core/`.
 */
import type { Table } from 'dexie';
import type { Attachment } from '../db/types';

/** Only these. Raster formats a browser and a printer both handle predictably. */
export const ALLOWED_IMAGE_MIME = ['image/png', 'image/jpeg', 'image/webp'] as const;

/**
 * Deliberately NOT in `ALLOWED_IMAGE_MIME`.
 *
 * `image/svg+xml` is XML: it can carry `<script>`, external references and
 * embedded HTML, and this image is rendered into a printable document. It is
 * also a format users reach for first because it scales, so the refusal has to
 * explain itself rather than just reject.
 */
export const SVG_MIME = 'image/svg+xml';

/** 2 MB. A logo is a logo; anything larger is a photograph. */
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Smaller than the logo: a signature is a thin line of ink, and the point of a
 * signature image is that it is small and fast to print.
 */
export const SIGNATURE_MAX_BYTES = 512 * 1024;

export type BusinessImageKind = Extract<Attachment['kind'], 'LOGO' | 'SIGNATURE'>;

export function maxBytesFor(kind: BusinessImageKind): number {
  return kind === 'LOGO' ? LOGO_MAX_BYTES : SIGNATURE_MAX_BYTES;
}

/** The human name used in refusals, so a message names the thing that was picked. */
export function labelFor(kind: BusinessImageKind): string {
  return kind === 'LOGO' ? 'logo' : 'signature';
}

export type ImageRefusal = { readonly ok: false; readonly message: string };
export type ImageAcceptance = { readonly ok: true };
export type ImageCheck = ImageAcceptance | ImageRefusal;

/**
 * Decide whether a picked file may be stored.
 *
 * Pure, so the rules are testable without a browser and without a database, and
 * so the same answer is available to the screen before it writes anything.
 */
export function checkBusinessImage(
  file: { readonly type: string; readonly size: number },
  kind: BusinessImageKind,
): ImageCheck {
  const label = labelFor(kind);

  if (file.type === SVG_MIME) {
    return {
      ok: false,
      message:
        `That ${label} is an SVG. SVG files can contain scripts and outside ` +
        `references, and this image is printed onto tax invoices. Please use a ` +
        `PNG or JPEG.`,
    };
  }

  if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(file.type)) {
    return {
      ok: false,
      message:
        `A ${label} must be a PNG, JPEG or WebP image. ` +
        `"${file.type || 'that file'}" is not an image this app can print.`,
    };
  }

  const max = maxBytesFor(kind);
  if (file.size > max) {
    return {
      ok: false,
      message:
        `That ${label} is ${formatBytes(file.size)}. The limit is ` +
        `${formatBytes(max)} - a ${label} is small by nature, and anything larger ` +
        `slows down every invoice printed afterwards.`,
    };
  }

  return { ok: true };
}

/** For the refusal message. Deliberately not a formatter that can throw on 0. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export class AttachmentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AttachmentError';
  }
}

/** The tables this service touches. Narrow on purpose: nothing else is needed. */
export interface AttachmentTables {
  attachments: Table<Attachment, string>;
}

export interface SaveImageInput {
  readonly businessId: string;
  readonly kind: BusinessImageKind;
  readonly file: File;
  /** Injected, so the row is testable and `createdAt` is never ambient. */
  readonly now: string;
  readonly id: string;
}

/**
 * Store one business image, returning the row.
 *
 * The caller is responsible for pointing the business at it. Storing and
 * attaching are separate steps so that a failure between them leaves an orphan
 * row rather than a business referencing an image that was never written.
 */
export async function saveBusinessImage(
  tables: AttachmentTables,
  input: SaveImageInput,
): Promise<Attachment> {
  const check = checkBusinessImage(input.file, input.kind);
  if (!check.ok) throw new AttachmentError(check.message);

  const row: Attachment = {
    id: input.id,
    businessId: input.businessId,
    kind: input.kind,
    name: input.file.name || labelFor(input.kind),
    mimeType: input.file.type,
    sizeBytes: input.file.size,
    blob: input.file,
    createdAt: input.now,
  };

  await tables.attachments.put(row);
  return row;
}

/**
 * Read one attachment's bytes as a `data:` URL, or undefined if it is gone.
 *
 * SCOPED TO THE BUSINESS, ALWAYS.
 *
 * A missing id is an ordinary state - the row was deleted in an older version,
 * or a restore lost it - and printing an invoice must not stop because of it. So
 * this returns undefined rather than throwing, and the letterhead simply omits
 * the image. But a row belonging to ANOTHER company is never returned: company
 * B must not be able to print company A's logo by guessing an id.
 */
export async function readImageDataUrl(
  tables: AttachmentTables,
  businessId: string,
  attachmentId: string | undefined,
): Promise<string | undefined> {
  if (!attachmentId) return undefined;

  const row = await tables.attachments.get(attachmentId);
  if (!row || row.businessId !== businessId) return undefined;

  // Belt and braces. The row was validated on the way in, but a database that
  // has been through an import or a restore can hold anything at all.
  if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(row.mimeType)) return undefined;

  return blobToDataUrl(row.blob);
}

/**
 * Remove an attachment, if it belongs to this business.
 *
 * Returns whether anything was deleted, so a caller can tell "removed" from
 * "there was nothing there" - which is not a failure, and should not be
 * reported as one.
 */
export async function deleteBusinessImage(
  tables: AttachmentTables,
  businessId: string,
  attachmentId: string | undefined,
): Promise<boolean> {
  if (!attachmentId) return false;
  const row = await tables.attachments.get(attachmentId);
  if (!row || row.businessId !== businessId) return false;
  await tables.attachments.delete(attachmentId);
  return true;
}

/**
 * A Blob as a `data:` URL.
 *
 * Uses the Blob's OWN type. There is deliberately no second parameter: a
 * caller-supplied type is a way for a caller to claim a PNG while carrying
 * something else, and the Blob already knows.
 */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  // `arrayBuffer()` rather than `FileReader`.
  //
  // Both work in a browser, but `Blob.arrayBuffer()` is the modern one and it
  // does not depend on `blob instanceof <this realm's> Blob` - a check that
  // fails for any Blob that has crossed a realm or been structured-cloned, and
  // so makes this function testable only in a browser. `FileReader` here threw
  // exactly that way under fake-indexeddb.
  const bytes = new Uint8Array(await blob.arrayBuffer());

  // Built in chunks. `String.fromCharCode(...bytes)` on a multi-megabyte logo
  // blows the argument limit; the chunked loop cannot.
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }

  // The Blob's OWN type, and always data:...;base64 - the one form every
  // renderer, every browser and every print path understands.
  return `data:${blob.type};base64,${btoa(binary)}`;
}